import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface IdleWarningModalProps {
  open: boolean;
  onStay: () => void;
  /** Seconds remaining until lockout when the modal opens. Drives the live
   *  countdown, which is reset to this value each time the modal re-opens and
   *  reaches 0:00 as the idle timer fires its lockout. */
  countdownSeconds: number;
}

export function IdleWarningModal({ open, onStay, countdownSeconds }: IdleWarningModalProps) {
  const [remaining, setRemaining] = useState(countdownSeconds);

  useEffect(() => {
    if (!open) return;
    // Reset to the full lead the moment the modal opens, then tick down once a
    // second. Functional update keeps the interval off the dependency list.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setRemaining(countdownSeconds);
    const id = window.setInterval(() => {
      setRemaining((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => {
      window.clearInterval(id);
    };
  }, [open, countdownSeconds]);

  const minutes = Math.floor(remaining / 60);
  const seconds = remaining % 60;
  const countdownLabel = `${minutes.toString()}:${seconds.toString().padStart(2, '0')}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Any close gesture (Escape, outside-click) counts as "stay signed in" —
        // it's user activity, and the only resolution that keeps the session.
        if (!next) onStay();
      }}
    >
      <DialogContent hideClose className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>You&rsquo;re about to be signed out</DialogTitle>
          <DialogDescription>
            You&rsquo;ll be signed out in{' '}
            <span className="font-mono tabular-nums">{countdownLabel}</span> due to inactivity. Stay
            signed in?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" onClick={onStay}>
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
